import { directSupabase } from '../../lib/supabaseClient';
import type { InstallEventRow } from './fitterJobList';
import { listJobSubcontractorNames } from './jobCard.service';
import {
  assembleOpenJobCards,
  type JobCardRow,
  type JobCompletionRow,
  type OpenJobCard,
  type OpenJobCardProject,
} from './openJobCards.model';

function table(name: string) {
  return (directSupabase as any).from(name);
}

async function profileNames(profileIds: string[]): Promise<Map<string, string>> {
  const names = new Map<string, string>();
  const unique = Array.from(new Set(profileIds.filter(Boolean)));
  if (!unique.length) return names;
  const { data } = await directSupabase.from('profiles').select('id, full_name, email').in('id', unique);
  ((data || []) as Array<{ id: string; full_name?: string | null; email?: string | null }>).forEach((row) => {
    names.set(row.id, row.full_name || row.email || 'Crew');
  });
  return names;
}

/** Every completion event, or only those for the given workspaces, with the attester's name. */
export async function loadJobCardCompletions(projectIds?: string[]): Promise<JobCompletionRow[]> {
  const select = (columns: string) => {
    const query = table('project_job_completions').select(columns);
    return projectIds ? query.in('project_id', projectIds) : query;
  };
  if (projectIds && !projectIds.length) return [];
  let result = await select('job_card_id, action, job_complete, attested_at, attested_by, continued_on_card_id');
  if (result.error) result = await select('job_card_id, action, job_complete, attested_at, attested_by');
  if (result.error) {
    if (!/action/i.test(result.error.message)) throw new Error(result.error.message);
    result = await select('job_card_id, job_complete, attested_at, attested_by');
    if (result.error) throw new Error(result.error.message);
  }
  const rows = (result.data || []) as Array<JobCompletionRow & { attested_by?: string | null }>;
  const names = await profileNames(rows.map((row) => String(row.attested_by || '')));
  return rows.map((row) => ({ ...row, actor_name: names.get(String(row.attested_by || '')) || null }));
}

async function loadProjects(projectIds: string[]): Promise<OpenJobCardProject[]> {
  if (!projectIds.length) return [];
  const { data, error } = await directSupabase
    .from('projects')
    .select('id, project_name, customer_name, lifecycle_ref, install_date, metadata, customer_id, project_status, status, complete, workflow_tasks')
    .in('id', projectIds);
  if (error) throw new Error(error.message);
  const rows = (data || []) as Array<OpenJobCardProject & { customer_id?: string | number | null }>;
  const customerIds = Array.from(new Set(rows.map((row) => String(row.customer_id || '').trim()).filter(Boolean)));
  const addressByCustomer = new Map<string, string>();
  if (customerIds.length) {
    const { data: customers } = await directSupabase.from('customers').select('id, address').in('id', customerIds);
    ((customers || []) as Array<{ id: string | number; address?: string | null }>).forEach((row) => {
      addressByCustomer.set(String(row.id), String(row.address || '').trim());
    });
  }
  return rows.map((row) => ({
    ...row,
    site_address: addressByCustomer.get(String(row.customer_id || '')) || null,
  }));
}

async function loadAssignees(cardIds: string[]): Promise<Array<{ job_card_id: string; name: string }>> {
  if (!cardIds.length) return [];
  const { data, error } = await table('project_job_assignees')
    .select('job_card_id, profile_id, assigned_at')
    .in('job_card_id', cardIds)
    .order('assigned_at', { ascending: true });
  if (error) throw new Error(error.message);
  const rows = (data || []) as Array<{ job_card_id: string; profile_id: string }>;
  const [names, subcontractors] = await Promise.all([
    profileNames(rows.map((row) => row.profile_id)),
    listJobSubcontractorNames({ cardIds }),
  ]);
  return [
    ...rows.map((row) => ({ job_card_id: row.job_card_id, name: names.get(row.profile_id) || 'Crew' })),
    ...subcontractors,
  ];
}

export async function loadOpenJobCards(): Promise<OpenJobCard[]> {
  const { data, error } = await table('project_job_cards').select('*');
  if (error) throw new Error(error.message);
  const cards = (data || []) as JobCardRow[];
  const projectIds = Array.from(new Set(cards.map((card) => card.project_id)));
  const [completions, projects, assignees, eventsResult] = await Promise.all([
    loadJobCardCompletions(),
    loadProjects(projectIds),
    loadAssignees(cards.map((card) => card.id)),
    projectIds.length
      ? directSupabase
        .from('project_events')
        .select('project_id, event_date, end_date, metadata, status, created_at')
        .in('project_id', projectIds)
        .eq('event_type', 'installationDateConfirmation')
      : Promise.resolve({ data: [], error: null }),
  ]);
  if (eventsResult.error) throw new Error(eventsResult.error.message);
  return assembleOpenJobCards({
    cards,
    completions,
    projects,
    events: (eventsResult.data || []) as unknown as InstallEventRow[],
    assignees,
  });
}
